<template>
  <div>
    <h2>Подписание отчетов</h2>
    <div class="page-content-info d-flex align-items-center justify-content-between">
      <div>Найдено: <span>{{ totalCount }}</span></div>
    </div>
    <b-pagination v-model="currentPage" :total-rows="totalCount" :per-page="pageSize"></b-pagination>
    <el-table :data="kindergartenCalculationReports" style="width: 100%" v-loading="isLoading">
      <el-table-column
          type="index"
          label="#"
          width="50">
      </el-table-column>

      <el-table-column
          prop="accountingMonth"
          label="Расчетный месяц">
      </el-table-column>

      <el-table-column
          prop="accountingYear"
          label="Расчетный год">
      </el-table-column>

      <el-table-column
          label="Название файла">
        <template #default="scope">
          <a
              @click="handleDownload(scope.row.report.uuid, scope.row.report.fileName)"
              :class="{ 'active-link': scope.row.report.reportStatus === 'GENERATED', 'error-link': scope.row.report.reportStatus === 'ERROR' }"
          >
            {{ scope.row.report.fileName }}
          </a>
        </template>
      </el-table-column>

      <el-table-column
          label="Статус">
        <template #default="scope">
          <span v-if="scope.row.signStatus === 'SIGNED'">Подписан</span>
          <span v-if="scope.row.signStatus === 'REJECTED_BY_HEAD'" style="color: red;">Отклонен</span>
          <div v-else-if="scope.row.signStatus === 'NOT_SIGNED'">
            <div v-if="hasAllowanceToSign(scope.row.lmsId)">
              <el-button
                  v-if="!isSaving"
                  type="primary"
                  @click="onClickSignDocument(scope.row.report.uuid)">
                Подписать
              </el-button>
              <el-button v-else disabled>Загрузка...</el-button>
              <el-button
                  type="danger"
                  @click="onClickShowRejectDocumentModal(scope.row.id)">
                Отказать
              </el-button>
            </div>
            <div v-else>
              <span style="color: red;">{{ textProhibitionToSign(scope.row.lmsId) }}</span>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="showRejectDocumentModal" class="modal">
      <div class="modal-content">
        <h2>Отказ от подписание документа</h2>
        <form @submit.prevent="onClickRejectDocument">
          <b-form-group label="Укажите причину отказа">
            <b-form-textarea v-model="rejectComment"></b-form-textarea>
          </b-form-group>
          <b-button type="submit" variant="primary" :disabled="isSaving">
            {{ isSaving ? 'Сохраняется...' : 'Сохранить' }}
          </b-button>
        </form>
        <button class="close-modal-btn" @click="handleClose">Закрыть</button>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted, watch} from 'vue';
import {downloadFileBytes, downloadReportFile, kindergartenCalculationListForHead, rejectKindergartenCalculationReport} from '@/services/reportService';
import { KindergartenCalculationReport, RejectCalculationReportRequest } from '@/types/report';
import { ElMessage } from 'element-plus';
import {PageResponse} from "@/types/pageResponse";
import {convertFileBytesToBase64} from "@/services/fileService";
import signService from "@/services/ncaLayerService";
import {checkAllowanceToSign, signDocument} from "@/services/signService";
import {AllowanceToSignResponse} from "@/types/kindergarten";

export default defineComponent({
  name: 'ReportSignView',
  setup() {
    const {signCallback} = signService;
    watch(() => signCallback.callback, (newVal) => {
      if (newVal) {
        newVal(signCallback.result);
      }
    });
    signService.init();

    const kindergartenCalculationReports = ref<KindergartenCalculationReport[]>([]);
    const response = ref<PageResponse<KindergartenCalculationReport>>();
    const currentPage = ref(1);
    let page = 0;
    const pageSize = 100;
    const totalCount = ref(0);
    const showRejectDocumentModal = ref(false);
    const rejectComment = ref("");
    const kindergartenCalculationId = ref(0);
    const checkAllowanceToSignMap = ref(new Map<string, AllowanceToSignResponse>());
    const isLoading = ref(false);

    const fetchReports = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          isLoading.value = true;
          response.value = await kindergartenCalculationListForHead(token, page, pageSize);
          kindergartenCalculationReports.value = response.value.content;
          totalCount.value =response.value.totalElements;
          response.value.content.forEach((content) => {
            if (content.signStatus == 'NOT_SIGNED') {
              fetchAllowanceToSign(content.lmsId, content.accountingMonth, content.accountingYear);
            }
          });
          isLoading.value = false;
        }
      } catch (error) {
        isLoading.value = false;
        console.error('Ошибка при получении отчетов:', error);
      }
    };

    const fetchAllowanceToSign = async (lmsId: string, accountingMonth : number, accountingYear : number) => {
      const request = await checkAllowanceToSign(lmsId, accountingMonth, accountingYear);
      checkAllowanceToSignMap.value.set(lmsId, request);
    }

    const handleDownload = async (fileId: string, fileName: string) => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          await downloadReportFile(token, fileId, fileName);
        }
      } catch (error) {
        console.error("Ошибка при скачивании файла:", error);
      }
    };

    const handleClose = () => {
      showRejectDocumentModal.value = false;
    };

    const onClickShowRejectDocumentModal = (id: number) => {
      kindergartenCalculationId.value = id;
      showRejectDocumentModal.value = true;
    }

    const onClickRejectDocument = () => {
      const token = localStorage.getItem("access_token");
      const data: RejectCalculationReportRequest = new RejectCalculationReportRequest(kindergartenCalculationId.value, rejectComment.value);
      if (token) {
        rejectKindergartenCalculationReport(token, data).then((response) => {
          console.log(response);
          if (response.status === 200) {
            ElMessage.success("Запрос успешно отправлен");
            window.location.reload();
          } else {
            ElMessage.error("Ошибка! Попробуйте еще раз");
          }
        })
      }
    }

    const hasAllowanceToSign = (lmsId: string) => {
      const allowanceToSign = checkAllowanceToSignMap.value.get(lmsId);
      if (allowanceToSign) {
        return allowanceToSign.allowanceToSign
      }
      return false;
    }

    const textProhibitionToSign = (lmsId: string) => {
      const allowanceToSign = checkAllowanceToSignMap.value.get(lmsId);
      if (allowanceToSign) {
        return allowanceToSign.textProhibitionToSign
      }
      return "Загрузка...";
    }

    onMounted(() => {
      fetchReports();
    });

    watch(currentPage, (newPage) => {
      page = newPage - 1;
      fetchReports()
    });

    return {
      kindergartenCalculationReports,
      totalCount,
      pageSize,
      currentPage,
      handleDownload,
      signService,
      showRejectDocumentModal,
      rejectComment,
      kindergartenCalculationId,
      isLoading,
      handleClose,
      onClickShowRejectDocumentModal,
      onClickRejectDocument,
      fetchReports,
      hasAllowanceToSign,
      textProhibitionToSign,
    };
  },
  data() {
    return {
      fileId: "",
      isSaving: false,
    };
  },
  methods: {
    async onClickSignDocument(fileId: string) {
      this.isSaving = true;
      this.fileId = fileId;
      const token = localStorage.getItem("access_token");
      if (token) {
        const fileBytes = await downloadFileBytes(token, fileId);
        const base64ToSign = await convertFileBytesToBase64(fileBytes);
        this.signService.createCMSSignatureFromBase64(this.getSelectedStorage(), 'SIGNATURE', base64ToSign, false, this.onSignDocumentCallback);
      }
    },
    getSelectedStorage() {
      return localStorage.getItem('selectedStorage') || 'PKCS12';
    },
    onSignDocumentCallback: async function (response: any) {
      if (response.status) {
        const cms = response.result;
        const token = localStorage.getItem("access_token");
        if (token) {
          try {
            await signDocument(token, this.fileId, cms).then();
            ElMessage.success("Документ успешно подписан");
            this.isSaving = false;
            await this.fetchReports();
          } catch (error: any) {
            this.isSaving = false;
            if (error && error.message) {
              console.error('Error message:', error?.message);
              ElMessage.error(error.message);
            } else {
              console.error('Unexpected error:', error);
            }
          }
        }
      }
    }
  }
});
</script>

<style scoped>
.modal {
  display: block;
  position: fixed;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 400px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

h2 {
  text-align: center;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-group input {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.close-modal-btn {
  margin-top: 10px;
  width: 100%;
  padding: 10px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.close-modal-btn:hover {
  background-color: #e31b0c;
}
</style>